/* ============================================================
   bundle-wishlist-guard.js   Bundle module   wishlist guard
   ============================================================
   Bundle "products" are display-only dummies that exist so the ERP can sync;
   buying happens on the builder page, which adds the real component variants
   (with sizes, troop numbers, ...) to the cart. The product page redirects to
   the builder (sections/main-product.liquid), so the dummy is unreachable
   through the theme - but the XB Wishlist app renders its own "Add to cart"
   buttons and its own cart calls, letting a shopper buy the un-configured
   dummy straight off their wishlist.

   Three layers close that gap, in decreasing order of politeness:

   1. Rewrite: any app-rendered "Add to cart" control in a row that links to a
      bundle product becomes a "Customize" link to the product page (which
      immediately forwards to the builder).
   2. Intercept: a capture-phase click handler catches an add control the
      rewrite hasn't reached yet (the observer is async) and navigates to the
      product page instead.
   3. Sweep: after any cart mutation, dummy bundle lines are removed from the
      cart and the shopper is sent to the product page to configure for real.
      This is endpoint-agnostic, mirroring the restricted-product sweep in
      layout/theme.liquid, so it catches adds fired by the app's own JS that
      never touch a DOM we can see.

   Config comes from window.AHG_BUNDLE_GUARD, emitted by
   snippets/bundle-wishlist-guard.liquid.

   NOTE: like the restricted-product gating, this is UX, not security - it
   runs in the browser. The worst a bypass yields is an un-configured dummy
   line that staff catch at fulfilment, same as today.
*/
(function () {
  'use strict';

  var cfg = window.AHG_BUNDLE_GUARD;
  if (!cfg || !cfg.handles || !cfg.handles.length) return;

  var bundleHandles = {};
  cfg.handles.forEach(function (h) {
    if (h) bundleHandles[String(h).toLowerCase()] = true;
  });

  var customizeLabel = cfg.label || 'Customize';

  function handleFromUrl(url) {
    var m = /\/products\/([a-z0-9_-]+)/i.exec(String(url || ''));
    return m ? m[1].toLowerCase() : null;
  }

  function isBundleHandle(handle) {
    return !!(handle && bundleHandles[String(handle).toLowerCase()]);
  }

  // The rewrite/intercept layers exist ONLY for the wishlist app's UI - the
  // theme's own buttons (bundle builders included: #ub-add reads "Add to
  // Cart") must never be touched. A control is in scope only inside an
  // element the app namespaces (XB Wishlist renders as xb-/xo-/wishlist-
  // prefixed markup). The cart sweep stays global: it keys off cart line
  // handles, which the theme can never legitimately add.
  var WISHLIST_SCOPE = '[class*="wishlist" i], [id*="wishlist" i], [data-wishlist], [class*="xb-" i], [id*="xb-" i], [class*="xo-" i], [id*="xo-" i]';

  function inWishlistUi(el) {
    try {
      return !!(el && el.closest && el.closest(WISHLIST_SCOPE));
    } catch (err) {
      return false;
    }
  }

  // Resolve the bundle product URL for the wishlist row an element sits in.
  // The app's markup is not ours to rely on, so instead of naming its classes
  // we walk up from the element until we find product links: if any of them is
  // a bundle, this row is that bundle's; if the row has product links and none
  // is a bundle, it's an ordinary product and we stop widening (going further
  // up would reach a container holding OTHER rows' links).
  function rowBundleUrl(el) {
    var node = el;
    for (var depth = 0; node && node !== document.documentElement && depth < 10; depth++) {
      if (node.querySelectorAll) {
        var attrHandle = node.getAttribute && (node.getAttribute('data-product-handle') || node.getAttribute('data-handle'));
        if (attrHandle) {
          return isBundleHandle(attrHandle) ? '/products/' + attrHandle.toLowerCase() : null;
        }

        var links = node.querySelectorAll('a[href*="/products/"], [data-product-handle], [data-handle]');
        var distinct = {};
        var only = null;
        for (var i = 0; i < links.length; i++) {
          var handle = links[i].hasAttribute('href')
            ? handleFromUrl(links[i].getAttribute('href'))
            : String(links[i].getAttribute('data-product-handle') || links[i].getAttribute('data-handle') || '').toLowerCase();
          if (!handle) continue;
          distinct[handle] = true;
          only = handle;
        }
        // Exactly one product referenced: this is the row, and it decides.
        // Several distinct products: we've widened past the row into a list -
        // never guess (that once sent a vest-bundle button to a trailblazer
        // page); the cart sweep is the backstop for anything missed here.
        var count = Object.keys(distinct).length;
        if (count === 1) return isBundleHandle(only) ? '/products/' + only : null;
        if (count > 1) return null;
      }
      node = node.parentElement;
    }
    return null;
  }

  function looksLikeAddControl(el) {
    if (!el || el.hasAttribute('data-ahg-bundle-customize')) return false;
    // Leave the theme's own product forms alone - the dummy's product page
    // redirects before one can render, and theme forms are governed elsewhere.
    if (el.closest && el.closest('form[action*="/cart/add"]')) return false;

    var text = (el.textContent || el.value || '').trim().toLowerCase();
    if (/\badd(ed)?\s+(all\s+)?to\s+(cart|bag)\b/.test(text)) return true;

    var attrs = [
      String(el.className || ''),
      el.getAttribute('name') || '',
      el.getAttribute('data-action') || '',
      el.getAttribute('aria-label') || ''
    ].join(' ').toLowerCase();
    return /add[-_\s]?to[-_\s]?cart|\batc\b/.test(attrs);
  }

  // --- Layer 1: rewrite app buttons into Customize links -------------------

  function swapToCustomizeLink(btn, url) {
    var link = document.createElement('a');
    link.href = url;
    link.textContent = customizeLabel;
    // Inherit the app's button classes so the link keeps its styling.
    link.className = String(btn.className || '');
    link.style.textDecoration = 'none';
    link.style.textAlign = 'center';
    link.setAttribute('data-ahg-bundle-customize', '');
    if (btn.parentNode) btn.parentNode.replaceChild(link, btn);
  }

  function rewriteAddControls(root) {
    if (!root || !root.querySelectorAll) return;
    var candidates = root.querySelectorAll('button, a, [role="button"], input[type="submit"], input[type="button"]');
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (!inWishlistUi(el) || !looksLikeAddControl(el)) continue;
      var url = rowBundleUrl(el);
      if (url) swapToCustomizeLink(el, url);
    }
  }

  var rescanTimer = null;
  function scheduleRescan() {
    if (rescanTimer) return;
    rescanTimer = setTimeout(function () {
      rescanTimer = null;
      rewriteAddControls(document);
    }, 100);
  }

  function start() {
    rewriteAddControls(document);
    // The wishlist app renders after load and re-renders on its own events.
    new MutationObserver(scheduleRescan).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // --- Layer 2: capture-phase click intercept ------------------------------
  // Capture on document fires before the app's own listeners on the button,
  // whatever order the scripts loaded in.

  document.addEventListener('click', function (e) {
    var el = e.target && e.target.closest
      ? e.target.closest('button, a, [role="button"], input[type="submit"], input[type="button"]')
      : null;
    if (!el) return;

    var url = null;
    if (el.hasAttribute && el.hasAttribute('data-ahg-bundle-customize')) {
      return; // already a plain link to the product page - let it navigate
    }
    if (inWishlistUi(el) && looksLikeAddControl(el)) url = rowBundleUrl(el);
    if (!url) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    window.location.href = url;
  }, true);

  // An app add that goes through a real <form action="/cart/add"> wraps the
  // whole row, product link included, so the form subtree alone is checked -
  // no walking up, which on a theme product page could reach an unrelated
  // bundle link elsewhere in the section and misfire.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.action || String(form.action).indexOf('/cart/add') === -1) return;
    if (!form.querySelectorAll || !inWishlistUi(form)) return;

    var refs = form.querySelectorAll('a[href*="/products/"], [data-product-handle], [data-handle]');
    for (var i = 0; i < refs.length; i++) {
      var handle = refs[i].hasAttribute('href')
        ? handleFromUrl(refs[i].getAttribute('href'))
        : String(refs[i].getAttribute('data-product-handle') || refs[i].getAttribute('data-handle') || '').toLowerCase();
      if (isBundleHandle(handle)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        window.location.href = '/products/' + handle;
        return;
      }
    }
  }, true);

  // --- Layer 3: cart sweep -------------------------------------------------
  // However a dummy line got in (app fetch, XHR, permalink), remove it and
  // send the shopper to the builder. On the initial page-load sweep the line
  // is stale history, so it is removed quietly without yanking the shopper
  // to another page mid-browse.

  var originalFetch = window.fetch;
  var sweepInFlight = null;
  var redirecting = false;

  function refreshCartUi() {
    document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
  }

  function sweepCart(redirectAfter) {
    if (sweepInFlight || redirecting) return sweepInFlight || Promise.resolve();

    sweepInFlight = originalFetch.call(window, '/cart.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
      .then(function (res) { return res && res.ok ? res.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.items) return;

        var dummies = cart.items.filter(function (line) {
          return line && isBundleHandle(line.handle);
        });
        if (!dummies.length) return;

        // Sequential: parallel /cart/change.js calls can race the cart lock.
        var chain = Promise.resolve();
        dummies.forEach(function (line) {
          chain = chain.then(function () {
            return originalFetch.call(window, '/cart/change.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: line.key, quantity: 0 })
            }).catch(function () {});
          });
        });

        return chain.then(function () {
          refreshCartUi();
          if (redirectAfter && !redirecting) {
            redirecting = true;
            window.location.href = '/products/' + dummies[0].handle;
          }
        });
      })
      .catch(function () {})
      .then(function () { sweepInFlight = null; });

    return sweepInFlight;
  }

  var CART_MUTATION = /\/cart\/(add|update|change|clear)(\.js|\b)/;

  // Our own /cart/change.js reverts go through originalFetch, so they never
  // re-enter this wrapper.
  if (originalFetch) {
    window.fetch = function (url) {
      var isMutation = false;
      try {
        var u = (typeof url === 'string') ? url : (url && url.url) || '';
        isMutation = CART_MUTATION.test(u);
      } catch (err) {}

      var result = originalFetch.apply(this, arguments);
      if (!isMutation || !result || typeof result.then !== 'function') return result;

      return result.then(function (response) {
        try {
          if (response && response.ok) sweepCart(true);
        } catch (err) {}
        return response;
      });
    };
  }

  var originalXHROpen = XMLHttpRequest.prototype.open;
  var originalXHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this._ahgBundleGuardUrl = url;
    return originalXHROpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    try {
      if (this._ahgBundleGuardUrl && CART_MUTATION.test(String(this._ahgBundleGuardUrl))) {
        this.addEventListener('load', function () {
          try {
            if (this.status >= 200 && this.status < 300) sweepCart(true);
          } catch (err) {}
        });
      }
    } catch (err) {}
    return originalXHRSend.apply(this, arguments);
  };

  // Initial sweep: catches lines added by a permalink navigation or left over
  // from a previous session. Quiet - no redirect mid-browse.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { sweepCart(false); });
  } else {
    sweepCart(false);
  }
})();
